import type { ReactNode } from "react";

import { StarIcon } from "@phosphor-icons/react";
import { Badge, Box, Flex, Grid, Text } from "@radix-ui/themes";

import type { PositionsWidgetRow } from "./PositionsWidget.types";
import {
  formatPositionMoney,
  POSITIONS_UNAVAILABLE_VALUE,
} from "./PositionsWidget.utils";

export interface PositionDetailPanelPresentationProps {
  row: PositionsWidgetRow;
  dayHigh?: number;
  dayLow?: number;
  isWatched?: boolean;
}

const joinRange = (left: string, right: string, separator: string): string =>
  left === POSITIONS_UNAVAILABLE_VALUE && right === POSITIONS_UNAVAILABLE_VALUE
    ? POSITIONS_UNAVAILABLE_VALUE
    : `${left} ${separator} ${right}`;

const openPnlDisplay = (row: PositionsWidgetRow): string =>
  row.unrealizedPlpc === POSITIONS_UNAVAILABLE_VALUE
    ? row.unrealizedPl
    : `${row.unrealizedPl} (${row.unrealizedPlpc})`;

/**
 * Label and value are separate blocks so they stack; as inline spans they ran
 * together (`COST BASIS$33,206.11`).
 */
const DetailMetric = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: PositionsWidgetRow["unrealizedPlColor"];
}) => {
  const unavailable = value === POSITIONS_UNAVAILABLE_VALUE;

  return (
    <Flex direction="column" gap="1">
      <Text as="div" size="1" color="gray" className="uppercase tracking-wide">
        {label}
      </Text>
      <Text
        as="div"
        size="2"
        color={unavailable ? "gray" : color}
        className="atd-numeric"
        aria-label={unavailable ? `${label} unavailable` : undefined}
      >
        {value}
      </Text>
    </Flex>
  );
};

const HeaderBadges = ({
  row,
  isWatched,
}: {
  row: PositionsWidgetRow;
  isWatched: boolean;
}): ReactNode => (
  <>
    <Badge size="1" color={row.sideColor} variant="soft">
      {row.side}
    </Badge>
    <Badge size="1" color="gray" variant="soft">
      {row.assetClass}
    </Badge>
    {isWatched ? (
      <Badge size="1" color="yellow" variant="soft">
        <Flex asChild align="center" gap="1">
          <span>
            <StarIcon aria-hidden size={10} weight="fill" />
            Watchlist
          </span>
        </Flex>
      </Badge>
    ) : null}
  </>
);

export const PositionDetailPanelPresentation = ({
  row,
  dayHigh,
  dayLow,
  isWatched = false,
}: PositionDetailPanelPresentationProps) => (
  <Box
    px="4"
    py="3"
    className="border-[color:var(--gray-a5)] border-t bg-[var(--color-surface)]"
  >
    <Flex align="center" gap="2" mb="3" wrap="wrap">
      <Text size="2" weight="bold">
        {row.symbol}
      </Text>
      <Text size="1" color="gray">
        {row.name}
      </Text>
      <HeaderBadges row={row} isWatched={isWatched} />
    </Flex>

    <Grid columns={{ initial: "2", sm: "3" }} gap="3">
      <DetailMetric label="Cost basis" value={row.costBasis} />
      <DetailMetric label="Market value" value={row.marketValue} />
      <DetailMetric
        label="Open P&L"
        value={openPnlDisplay(row)}
        color={row.unrealizedPlColor}
      />
      <DetailMetric
        label="Day P&L"
        value={row.unrealizedIntradayPl}
        color={row.unrealizedIntradayPlColor}
      />
      <DetailMetric
        label="Avg / Mark"
        value={joinRange(row.avgEntryPrice, row.currentPrice, "→")}
      />
      <DetailMetric
        label="Intraday range"
        value={joinRange(
          formatPositionMoney(dayLow),
          formatPositionMoney(dayHigh),
          "–",
        )}
      />
    </Grid>
  </Box>
);
